import { useEffect, useState, type FormEvent } from "react";
import type { AreaPractica, EtapaProceso, Materia, ProcesoTrabajo } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { Boton } from "../../components/Boton";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { Campo, Input, Select, TextArea } from "../../components/Formulario";
import { EstadoVacio } from "../../components/EstadoVacio";
import { Insignia } from "../../components/Insignia";
import { Modal } from "../../components/Modal";
import { SelectorMultiple } from "../../components/SelectorMultiple";
import { SelectorVistaProceso } from "../../components/SelectorVistaProceso";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { Tarjeta } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { useEstadoEnUrl } from "../../hooks/useEstadoEnUrl";
import { api, ErrorApi } from "../../lib/apiClient";
import { DisenadorVisualProceso } from "./DisenadorVisualProceso";
import { EditorAutomatizaciones, EditorChecklist, EditorDocumentosSugeridos } from "./EditoresEtapa";
import { PlantillaVistaPrevia } from "./PlantillaVistaPrevia";

interface UsuarioBasico {
  id: string;
  nombre_completo: string;
}

interface PlantillaProceso {
  clave: string;
  nombre: string;
  descripcion: string;
  cantidadEtapas: number;
}

// Configuración del Motor de Procesos de Trabajo: cada estudio define sus
// propios Procesos (ej. "Accidentes de Tránsito") y, dentro de cada uno,
// sus Etapas ordenadas (ej. "Ingreso del Lead" → ... → "Cierre"). Master
// (Procesos) - detalle (Etapas del proceso elegido) - sub-detalle (checklist/
// documentos sugeridos/automatizaciones de la etapa elegida). Ver
// migrations/0020_procesos_trabajo.sql y routes/procesos.ts.
export function ProcesosTrabajoPage() {
  const { permisos } = useAuth();
  const puedeAdministrar = permisos.has("procesos.administrar");
  const { mostrar } = useToast();
  const [procesos, setProcesos] = useState<ProcesoTrabajo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [materias, setMaterias] = useState<Materia[]>([]);
  const [areas, setAreas] = useState<AreaPractica[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioBasico[]>([]);
  const [plantillas, setPlantillas] = useState<PlantillaProceso[]>([]);

  const [procesoSeleccionado, setProcesoSeleccionado] = useState<ProcesoTrabajo | null>(null);
  const [etapas, setEtapas] = useState<EtapaProceso[]>([]);
  const [cargandoEtapas, setCargandoEtapas] = useState(false);
  const [etapaSeleccionada, setEtapaSeleccionada] = useState<EtapaProceso | null>(null);

  const [modalProcesoAbierto, setModalProcesoAbierto] = useState(false);
  const [procesoEnEdicion, setProcesoEnEdicion] = useState<ProcesoTrabajo | null>(null);
  const [valoresProceso, setValoresProceso] = useState({
    nombre: "",
    descripcion: "",
    color: "#64748b",
    orden: "0",
    bloquear_avance_si_checklist_pendiente: false,
  });
  const [materiaIds, setMateriaIds] = useState<Set<string>>(new Set());
  const [areaIds, setAreaIds] = useState<Set<string>>(new Set());
  const [guardandoProceso, setGuardandoProceso] = useState(false);

  const [modalPlantillasAbierto, setModalPlantillasAbierto] = useState(false);
  const [plantillaEnPreview, setPlantillaEnPreview] = useState<string | null>(null);
  const [duplicandoPlantilla, setDuplicandoPlantilla] = useState(false);

  // Proceso seleccionado y vista activa viven en la URL (?proceso=&vista=)
  // para sobrevivir a un "volver" desde el Kanban y al botón Atrás del
  // navegador — ver useEstadoEnUrl. Diseñador Visual (mapa de tarjetas) es
  // la vista por defecto; Vista Tabla (clásica, sin cambios) queda un clic
  // al lado para no romper compatibilidad.
  const [procesoIdUrl, setProcesoIdUrl] = useEstadoEnUrl("proceso", "");
  const [vistaUrl] = useEstadoEnUrl("vista", "visual");
  const vistaVisual = vistaUrl !== "tabla";

  const [modalEtapaAbierto, setModalEtapaAbierto] = useState(false);
  const [etapaEnEdicion, setEtapaEnEdicion] = useState<EtapaProceso | null>(null);
  const [valoresEtapa, setValoresEtapa] = useState({
    nombre: "",
    color: "#64748b",
    orden: "0",
    responsable_usuario_id: "",
    tiempo_objetivo_dias: "",
    descripcion: "",
  });
  const [guardandoEtapa, setGuardandoEtapa] = useState(false);

  async function cargarProcesos() {
    setCargando(true);
    try {
      const datos = await api.get<{ items: ProcesoTrabajo[] }>("/procesos");
      setProcesos(datos.items);
      setProcesoSeleccionado((actual) => (actual ? (datos.items.find((p) => p.id === actual.id) ?? actual) : actual));
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudieron cargar los procesos.", "error");
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargarProcesos();
    api
      .get<{ items: Materia[] }>("/catalogos/materias")
      .then((d) => setMaterias(d.items))
      .catch(() => {});
    api
      .get<{ items: AreaPractica[] }>("/catalogos-estudio/areas-practica")
      .then((d) => setAreas(d.items))
      .catch(() => {});
    api
      .get<{ items: UsuarioBasico[] }>("/usuarios")
      .then((d) => setUsuarios(d.items))
      .catch(() => {});
    api
      .get<{ items: PlantillaProceso[] }>("/procesos/plantillas")
      .then((d) => setPlantillas(d.items))
      .catch(() => {});
  }, []);

  async function cargarEtapas(procesoId: string) {
    setCargandoEtapas(true);
    setEtapaSeleccionada(null);
    try {
      const datos = await api.get<{ items: EtapaProceso[] }>(`/procesos/${procesoId}/etapas`);
      setEtapas(datos.items);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudieron cargar las etapas.", "error");
    } finally {
      setCargandoEtapas(false);
    }
  }

  function seleccionarProceso(proceso: ProcesoTrabajo) {
    setProcesoSeleccionado(proceso);
    if (proceso.id !== procesoIdUrl) setProcesoIdUrl(proceso.id);
    cargarEtapas(proceso.id);
  }

  // Restaura el proceso seleccionado si se llega con ?proceso= en la URL
  // (deep-link, volver desde el Kanban, botón Atrás del navegador).
  useEffect(() => {
    if (!procesoIdUrl || procesoSeleccionado?.id === procesoIdUrl) return;
    const encontrado = procesos.find((p) => p.id === procesoIdUrl);
    if (encontrado) seleccionarProceso(encontrado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procesoIdUrl, procesos]);

  function abrirAltaProceso() {
    setProcesoEnEdicion(null);
    setValoresProceso({
      nombre: "",
      descripcion: "",
      color: "#64748b",
      orden: String(procesos.length + 1),
      bloquear_avance_si_checklist_pendiente: false,
    });
    setMateriaIds(new Set());
    setAreaIds(new Set());
    setModalProcesoAbierto(true);
  }

  function abrirEdicionProceso(proceso: ProcesoTrabajo) {
    setProcesoEnEdicion(proceso);
    setValoresProceso({
      nombre: proceso.nombre,
      descripcion: proceso.descripcion ?? "",
      color: proceso.color ?? "#64748b",
      orden: String(proceso.orden),
      bloquear_avance_si_checklist_pendiente: proceso.bloquear_avance_si_checklist_pendiente,
    });
    setMateriaIds(new Set(proceso.materiaIds ?? []));
    setAreaIds(new Set(proceso.areaPracticaIds ?? []));
    setModalProcesoAbierto(true);
  }

  async function guardarProceso(e: FormEvent) {
    e.preventDefault();
    setGuardandoProceso(true);
    try {
      const cuerpo = {
        nombre: valoresProceso.nombre,
        descripcion: valoresProceso.descripcion || null,
        color: valoresProceso.color || null,
        orden: Number(valoresProceso.orden),
        bloquear_avance_si_checklist_pendiente: valoresProceso.bloquear_avance_si_checklist_pendiente,
        materiaIds: [...materiaIds],
        areaPracticaIds: [...areaIds],
      };
      if (procesoEnEdicion) {
        await api.put(`/procesos/${procesoEnEdicion.id}`, cuerpo);
      } else {
        await api.post("/procesos", cuerpo);
      }
      mostrar("Proceso guardado.", "exito");
      setModalProcesoAbierto(false);
      cargarProcesos();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar el proceso.", "error");
    } finally {
      setGuardandoProceso(false);
    }
  }

  async function alternarActivoProceso(proceso: ProcesoTrabajo) {
    try {
      await api.patch(`/procesos/${proceso.id}/activo`, { activo: !proceso.activo });
      cargarProcesos();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  async function duplicarPlantilla(clave: string) {
    setDuplicandoPlantilla(true);
    try {
      const datos = await api.post<{ proceso: ProcesoTrabajo }>(`/procesos/plantillas/${clave}/duplicar`, {});
      mostrar("Plantilla duplicada como nuevo proceso. Ya podés editarlo.", "exito");
      setModalPlantillasAbierto(false);
      setPlantillaEnPreview(null);
      await cargarProcesos();
      seleccionarProceso(datos.proceso);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo duplicar la plantilla.", "error");
    } finally {
      setDuplicandoPlantilla(false);
    }
  }

  function abrirAltaEtapa() {
    setEtapaEnEdicion(null);
    setValoresEtapa({
      nombre: "",
      color: "#64748b",
      orden: String(etapas.length + 1),
      responsable_usuario_id: "",
      tiempo_objetivo_dias: "",
      descripcion: "",
    });
    setModalEtapaAbierto(true);
  }

  function abrirEdicionEtapa(etapa: EtapaProceso) {
    setEtapaEnEdicion(etapa);
    setValoresEtapa({
      nombre: etapa.nombre,
      color: etapa.color ?? "#64748b",
      orden: String(etapa.orden),
      responsable_usuario_id: etapa.responsable_usuario_id ?? "",
      tiempo_objetivo_dias: etapa.tiempo_objetivo_dias != null ? String(etapa.tiempo_objetivo_dias) : "",
      descripcion: etapa.descripcion ?? "",
    });
    setModalEtapaAbierto(true);
  }

  async function guardarEtapa(e: FormEvent) {
    e.preventDefault();
    if (!procesoSeleccionado) return;
    setGuardandoEtapa(true);
    try {
      const cuerpo = {
        nombre: valoresEtapa.nombre,
        color: valoresEtapa.color || null,
        orden: Number(valoresEtapa.orden),
        responsable_usuario_id: valoresEtapa.responsable_usuario_id || null,
        tiempo_objetivo_dias: valoresEtapa.tiempo_objetivo_dias ? Number(valoresEtapa.tiempo_objetivo_dias) : null,
        descripcion: valoresEtapa.descripcion || null,
      };
      if (etapaEnEdicion) {
        await api.put(`/procesos/etapas/${etapaEnEdicion.id}`, cuerpo);
      } else {
        await api.post(`/procesos/${procesoSeleccionado.id}/etapas`, cuerpo);
      }
      mostrar("Etapa guardada.", "exito");
      setModalEtapaAbierto(false);
      cargarEtapas(procesoSeleccionado.id);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar la etapa.", "error");
    } finally {
      setGuardandoEtapa(false);
    }
  }

  async function alternarActivoEtapa(etapa: EtapaProceso) {
    try {
      await api.patch(`/procesos/etapas/${etapa.id}/activo`, { activo: !etapa.activo });
      if (procesoSeleccionado) cargarEtapas(procesoSeleccionado.id);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  const columnasProcesos: ColumnaTabla<ProcesoTrabajo>[] = [
    {
      encabezado: "Nombre",
      render: (p) => (
        <span className="flex items-center gap-2">
          <span
            className="h-3 w-3 shrink-0 rounded-full border border-borde"
            style={{ backgroundColor: p.color ?? undefined }}
          />
          {p.nombre}
        </span>
      ),
    },
    { encabezado: "Orden", render: (p) => p.orden },
    { encabezado: "Estado", render: (p) => <Insignia texto={p.activo ? "Activo" : "Inactivo"} /> },
    {
      encabezado: "",
      render: (p) =>
        puedeAdministrar ? (
          <div className="flex justify-end gap-2">
            <Boton variante="secundario" tamano="sm" onClick={() => abrirEdicionProceso(p)}>
              Editar
            </Boton>
            <Boton variante="secundario" tamano="sm" onClick={() => alternarActivoProceso(p)}>
              {p.activo ? "Desactivar" : "Activar"}
            </Boton>
          </div>
        ) : null,
      className: "text-right",
    },
  ];

  const columnasEtapas: ColumnaTabla<EtapaProceso>[] = [
    {
      encabezado: "Nombre",
      render: (et) => (
        <span className="flex items-center gap-2">
          <span
            className="h-3 w-3 shrink-0 rounded-full border border-borde"
            style={{ backgroundColor: et.color ?? undefined }}
          />
          {et.nombre}
        </span>
      ),
    },
    { encabezado: "Orden", render: (et) => et.orden },
    { encabezado: "Responsable por defecto", render: (et) => et.responsable_nombre ?? "—" },
    { encabezado: "Tiempo objetivo", render: (et) => (et.tiempo_objetivo_dias != null ? `${et.tiempo_objetivo_dias} días` : "—") },
    { encabezado: "Estado", render: (et) => <Insignia texto={et.activo ? "Activo" : "Inactivo"} /> },
    {
      encabezado: "",
      render: (et) =>
        puedeAdministrar ? (
          <div className="flex justify-end gap-2">
            <Boton variante="secundario" tamano="sm" onClick={() => abrirEdicionEtapa(et)}>
              Editar
            </Boton>
            <Boton variante="secundario" tamano="sm" onClick={() => alternarActivoEtapa(et)}>
              {et.activo ? "Desactivar" : "Activar"}
            </Boton>
          </div>
        ) : null,
      className: "text-right",
    },
  ];

  return (
    <div>
      <EncabezadoPagina
        titulo="Procesos de Trabajo"
        descripcion="Cada estudio configura sus propios flujos internos (Procesos) y los pasos ordenados dentro de cada uno (Etapas), sin programar nada. Se usan en el Kanban y en la pestaña Proceso de cada expediente."
        volver={{ rutaPadre: "/configuracion", etiqueta: "Volver a Configuración" }}
        migas={[
          { etiqueta: "Configuración", ruta: "/configuracion" },
          { etiqueta: "Procesos de Trabajo" },
        ]}
      />

      <Tarjeta className="mb-6 p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-texto">Procesos</h3>
            <p className="text-xs text-texto-secundario">
              Elegí un proceso de la lista para configurar sus etapas.
            </p>
          </div>
          {puedeAdministrar && (
            <div className="flex gap-2">
              <Boton variante="secundario" onClick={() => setModalPlantillasAbierto(true)}>
                Usar una plantilla
              </Boton>
              <Boton onClick={abrirAltaProceso}>+ Nuevo proceso</Boton>
            </div>
          )}
        </div>
        <Tabla
          columnas={columnasProcesos}
          filas={procesos}
          claveFila={(p) => p.id}
          cargando={cargando}
          tituloVacio="Sin procesos configurados"
          onFilaClick={seleccionarProceso}
        />
      </Tarjeta>

      {procesoSeleccionado && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <SelectorVistaProceso procesoId={procesoSeleccionado.id} vistaActual={vistaVisual ? "visual" : "tabla"} />
          </div>

          {vistaVisual ? (
            <DisenadorVisualProceso
              proceso={procesoSeleccionado}
              etapas={etapas}
              puedeAdministrar={puedeAdministrar}
              onCambio={() => cargarEtapas(procesoSeleccionado.id)}
              onEditarProceso={() => abrirEdicionProceso(procesoSeleccionado)}
              onProcesoDuplicado={(p) => {
                cargarProcesos();
                seleccionarProceso(p);
              }}
              onEditarEtapa={abrirEdicionEtapa}
              onNuevaEtapa={abrirAltaEtapa}
              onUsarPlantilla={() => setModalPlantillasAbierto(true)}
            />
          ) : (
            <Tarjeta className="p-5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-texto">Etapas de "{procesoSeleccionado.nombre}"</h3>
                  <p className="text-xs text-texto-secundario">
                    Hacé clic en una etapa para configurar su checklist, documentos sugeridos y automatizaciones.
                  </p>
                </div>
                {puedeAdministrar && <Boton onClick={abrirAltaEtapa}>+ Nueva etapa</Boton>}
              </div>
              <Tabla
                columnas={columnasEtapas}
                filas={etapas}
                claveFila={(et) => et.id}
                cargando={cargandoEtapas}
                tituloVacio="Sin etapas configuradas"
                onFilaClick={setEtapaSeleccionada}
              />

              {etapaSeleccionada && (
                <div className="mt-6 border-t border-borde pt-6">
                  <h4 className="mb-4 text-sm font-semibold text-texto">Detalle de "{etapaSeleccionada.nombre}"</h4>
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <EditorChecklist
                      etapaId={etapaSeleccionada.id}
                      puedeAdministrar={puedeAdministrar}
                      onError={(m) => mostrar(m, "error")}
                      onExito={() => mostrar("Checklist guardado.", "exito")}
                    />
                    <EditorDocumentosSugeridos
                      etapaId={etapaSeleccionada.id}
                      puedeAdministrar={puedeAdministrar}
                      onError={(m) => mostrar(m, "error")}
                      onExito={() => mostrar("Documentos sugeridos guardados.", "exito")}
                    />
                    <EditorAutomatizaciones
                      etapaId={etapaSeleccionada.id}
                      puedeAdministrar={puedeAdministrar}
                      onError={(m) => mostrar(m, "error")}
                      onExito={() => mostrar("Automatizaciones guardadas.", "exito")}
                    />
                  </div>
                </div>
              )}
            </Tarjeta>
          )}
        </div>
      )}

      <Modal
        titulo={procesoEnEdicion ? "Editar proceso" : "Nuevo proceso"}
        abierto={modalProcesoAbierto}
        onCerrar={() => setModalProcesoAbierto(false)}
      >
        <form onSubmit={guardarProceso} className="space-y-4">
          <Campo etiqueta="Nombre" obligatorio>
            <Input
              required
              value={valoresProceso.nombre}
              onChange={(e) => setValoresProceso((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Descripción">
            <TextArea
              rows={2}
              value={valoresProceso.descripcion}
              onChange={(e) => setValoresProceso((v) => ({ ...v, descripcion: e.target.value }))}
            />
          </Campo>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Color">
              <Input
                type="color"
                value={valoresProceso.color}
                onChange={(e) => setValoresProceso((v) => ({ ...v, color: e.target.value }))}
                className="h-10 w-20 p-1"
              />
            </Campo>
            <Campo etiqueta="Orden">
              <Input
                type="number"
                value={valoresProceso.orden}
                onChange={(e) => setValoresProceso((v) => ({ ...v, orden: e.target.value }))}
              />
            </Campo>
          </div>
          <Campo etiqueta="Materias aplicables">
            <SelectorMultiple
              opciones={materias.map((m) => ({ id: m.id, etiqueta: m.nombre }))}
              seleccionados={materiaIds}
              onCambiar={(id) =>
                setMateriaIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
              vacio="No hay materias disponibles."
            />
          </Campo>
          <Campo etiqueta="Unidades de Negocio aplicables">
            <SelectorMultiple
              opciones={areas.map((a) => ({ id: a.id, etiqueta: a.nombre }))}
              seleccionados={areaIds}
              onCambiar={(id) =>
                setAreaIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
              vacio="No hay unidades de negocio disponibles."
            />
          </Campo>
          <label className="flex items-center gap-2 text-sm text-texto">
            <input
              type="checkbox"
              checked={valoresProceso.bloquear_avance_si_checklist_pendiente}
              onChange={(e) =>
                setValoresProceso((v) => ({ ...v, bloquear_avance_si_checklist_pendiente: e.target.checked }))
              }
            />
            No permitir avanzar de etapa si hay ítems obligatorios del checklist sin completar
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Boton type="button" variante="secundario" onClick={() => setModalProcesoAbierto(false)} disabled={guardandoProceso}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoProceso}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal titulo="Usar una plantilla" abierto={modalPlantillasAbierto} onCerrar={() => setModalPlantillasAbierto(false)}>
        {plantillas.length === 0 ? (
          <EstadoVacio titulo="No hay plantillas disponibles" />
        ) : (
          <ul className="divide-y divide-borde">
            {plantillas.map((p) => (
              <li key={p.clave} className="flex items-center justify-between gap-3 py-3.5 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-texto">{p.nombre}</p>
                  <p className="text-xs text-texto-secundario">
                    {p.descripcion} · {p.cantidadEtapas} etapas
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Boton variante="secundario" tamano="sm" onClick={() => setPlantillaEnPreview(p.clave)}>
                    Vista previa
                  </Boton>
                  <Boton tamano="sm" onClick={() => duplicarPlantilla(p.clave)} cargando={duplicandoPlantilla}>
                    Duplicar
                  </Boton>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      <Modal
        titulo={etapaEnEdicion ? "Editar etapa" : "Nueva etapa"}
        abierto={modalEtapaAbierto}
        onCerrar={() => setModalEtapaAbierto(false)}
      >
        <form onSubmit={guardarEtapa} className="space-y-4">
          <Campo etiqueta="Nombre" obligatorio>
            <Input
              required
              value={valoresEtapa.nombre}
              onChange={(e) => setValoresEtapa((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Color">
              <Input
                type="color"
                value={valoresEtapa.color}
                onChange={(e) => setValoresEtapa((v) => ({ ...v, color: e.target.value }))}
                className="h-10 w-20 p-1"
              />
            </Campo>
            <Campo etiqueta="Orden">
              <Input
                type="number"
                value={valoresEtapa.orden}
                onChange={(e) => setValoresEtapa((v) => ({ ...v, orden: e.target.value }))}
              />
            </Campo>
          </div>
          <Campo etiqueta="Responsable por defecto">
            <Select
              value={valoresEtapa.responsable_usuario_id}
              onChange={(e) => setValoresEtapa((v) => ({ ...v, responsable_usuario_id: e.target.value }))}
            >
              <option value="">Sin responsable por defecto</option>
              {usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre_completo}
                </option>
              ))}
            </Select>
          </Campo>
          <Campo etiqueta="Tiempo objetivo (días)">
            <Input
              type="number"
              min={0}
              value={valoresEtapa.tiempo_objetivo_dias}
              onChange={(e) => setValoresEtapa((v) => ({ ...v, tiempo_objetivo_dias: e.target.value }))}
              placeholder="Sin límite"
            />
          </Campo>
          <Campo etiqueta="Descripción">
            <TextArea
              rows={2}
              value={valoresEtapa.descripcion}
              onChange={(e) => setValoresEtapa((v) => ({ ...v, descripcion: e.target.value }))}
            />
          </Campo>
          <div className="flex justify-end gap-2 pt-2">
            <Boton type="button" variante="secundario" onClick={() => setModalEtapaAbierto(false)} disabled={guardandoEtapa}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoEtapa}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      {plantillaEnPreview && (
        <PlantillaVistaPrevia
          clave={plantillaEnPreview}
          onCerrar={() => setPlantillaEnPreview(null)}
          onDuplicar={duplicarPlantilla}
          duplicando={duplicandoPlantilla}
        />
      )}
    </div>
  );
}

